import React, { FC, useEffect, useState } from 'react';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'react-intl';
import PruTablePaginationActions from '../../../../../../../common/components/Table/PruTablePaginationActions';
import { CampaignSelectedSalesMaterialItem } from 'src/app/modules/PulseLeads/types/campaign-types';

export type SelectedSalesMaterialItem = CampaignSelectedSalesMaterialItem;

type SelectedSalesMaterialTableProps = {
  items: SelectedSalesMaterialItem[];
  disabled: boolean;
  onAddMaterial: () => void;
  onDelete: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
};

const ROWS_PER_PAGE = 10;

const useStyles = makeStyles()(() => ({
  wrapper: {
    width: '100%',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 12,
  },
  emptyButtonRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 36,
  },
  actionCell: {
    width: 140,
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  actionBtns: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 12,
  },
  moveBtn: {
    marginRight: 6,
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1px solid black',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
}));

const SelectedSalesMaterialTable: FC<SelectedSalesMaterialTableProps> = ({
  items,
  disabled,
  onAddMaterial,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [page, setPage] = useState(0);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(items.length / ROWS_PER_PAGE) - 1);

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [items.length, page]);

  const pageItems = items.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, nextPage: number) => {
    setPage(nextPage);
  };

  if (items.length === 0) {
    return (
      <div className={classes.emptyButtonRow}>
        <Button disabled={disabled} variant="contained" color="secondary" onClick={onAddMaterial}>
          {Translation('component.materialset.addNew.text')}
        </Button>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.buttonRow}>
        <Button disabled={disabled} variant="contained" color="secondary" onClick={onAddMaterial}>
          {Translation('component.materialset.addNew.text')}
        </Button>
      </div>
      <TableContainer component={Paper}>
        <Table aria-label="selected sales material table">
          <TableHead>
            <TableRow>
              <TableCell>{Translation('component.formLabel.name')}</TableCell>
              <TableCell>{Translation('pulseleads.campaign.salesMaterial.section')}</TableCell>
              <TableCell>{Translation('component.formLabel.type')}</TableCell>
              <TableCell>{Translation('component.formLabel.status')}</TableCell>
              <TableCell className={classes.actionCell}>{Translation('component.formLabel.action')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageItems.map((item, index) => {
              const originalIndex = page * ROWS_PER_PAGE + index;

              return (
                <TableRow key={item.uuid}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.section}</TableCell>
                  <TableCell>{item.type}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell className={classes.actionCell}>
                    {disabled ? (
                      '--'
                    ) : (
                      <>
                        <span className={classes.actionBtns}>
                          {originalIndex === 0 ? null : (
                            <ArrowUpward className={classes.moveBtn} onClick={() => onMoveUp(originalIndex)} />
                          )}
                          {originalIndex === items.length - 1 ? null : (
                            <ArrowDownward className={classes.moveBtn} onClick={() => onMoveDown(originalIndex)} />
                          )}
                        </span>
                        <Button onClick={() => onDelete(originalIndex)}>{Translation('app.button.delete')}</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={5}
                component="td"
                count={items.length}
                rowsPerPage={ROWS_PER_PAGE}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelRowsPerPage=""
                ActionsComponent={PruTablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SelectedSalesMaterialTable;
